import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INewUser, IPortalAlertMessage, ISecurityKey, IUpdateUser } from "src/common/typings/user";
import { RootState } from "../store";
import {
  addUserAction,
  getRolesAction,
  handleAddFail,
  handleAddUser,
  handleUpdateUser,
  handleUpdateFail,
  updateUserAction,
  getUserAction,
  handleGetUser,
  handleGetFail,
  deleteUserAction,
  handleDeleteFail,
  resetPasswordAction,
  getSecurityKeys,
  getRoleByNameAction,
  addUserRoleAction,
  handleGetRoleByNameFail,
  handleGetRoleByName,
  updateUserRoleAction,
  getCustomAlertData,
} from "./actions/managementActions";

export interface ManagementState {
  newUsers: INewUser[];
  status: "idle" | "loading" | "failed";
  roles: string[];
  securityKeys: ISecurityKey[];
  currentUser?: IUpdateUser;
  customAlertData?: any;
}

const initialState: ManagementState = {
  status: "idle",
  newUsers: [],
  roles: [],
  securityKeys: [],
  customAlertData: {},
};

export const managementSlice = createSlice({
  name: "management",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // loginActionSync: handleLogin,
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(addUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addUserAction.fulfilled, handleAddUser)
      .addCase(addUserAction.rejected, handleAddFail)

      .addCase(updateUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserAction.fulfilled, handleUpdateUser)
      .addCase(updateUserAction.rejected, handleUpdateFail)

      .addCase(getUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserAction.fulfilled, handleGetUser)
      .addCase(getUserAction.rejected, handleGetFail)

      .addCase(deleteUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUserAction.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteUserAction.rejected, handleDeleteFail)

      .addCase(resetPasswordAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(resetPasswordAction.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(resetPasswordAction.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(
        getRolesAction.fulfilled,
        (state: ManagementState, { payload }: PayloadAction<string[]>) => {
          state.roles = payload;
        }
      )
      .addCase(
        getSecurityKeys.fulfilled,
        (state: ManagementState, { payload }: PayloadAction<ISecurityKey[]>) => {
          state.securityKeys = payload;
        }
      )
      .addCase(
        getCustomAlertData.fulfilled,
        (state: ManagementState, { payload }: PayloadAction<IPortalAlertMessage>) => {
          state.customAlertData = payload;
        }
      )
      // Add User Role
      .addCase(addUserRoleAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addUserRoleAction.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(addUserRoleAction.rejected, (state) => {
        state.status = "failed";
      })
      // Get User Role By Name
      .addCase(getRoleByNameAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getRoleByNameAction.fulfilled, handleGetRoleByName)
      .addCase(getRoleByNameAction.rejected, handleGetRoleByNameFail)
      // Update User Role
      .addCase(updateUserRoleAction.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(updateUserRoleAction.pending, (state) => {
        state.status = "loading";
        // delete state.errorMessage;
      })
      .addCase(updateUserRoleAction.rejected, (state) => {
        state.status = "failed";
      });
  },
});

// export const { loginActionSync } = authSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectStatus = (state: RootState) => state.management.status;
export const selectUsers = (state: RootState) => state.management.newUsers;
export const selectSecurityKeys = (state: RootState) => state.management.securityKeys;
export const selectRoles = (state: RootState) => state.management.roles;
export const selectCurrentUser = (state: RootState) => state.management.currentUser;
export const selectCustomAlertData = (state: RootState) => state.management.customAlertData;
